import { Room } from '@era/locations-sdk';
import { AutomatedApplicationConfiguration, AutomatedConfiguration } from '@era/settings-sdk';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector, Selector } from '@ngrx/store';
import { selectAllAppleApplications } from '../apple-applications/apple-applications.selector';
import { CategoryStoreSelectors } from '../category';
import * as AutomatedRolloutsRouterStoreSelectors from '../router/rollout-router.selector';
import * as fromAutomatedRollouts from './automated-rollouts.reducer';
import { AutomatedRolloutsState } from './automated-rollouts.state';

export const selectAutomatedRolloutsState = createFeatureSelector<AutomatedRolloutsState>('automatedRollouts');

export const selectAutomatedRolloutIds = createSelector(
  selectAutomatedRolloutsState,
  fromAutomatedRollouts.selectAutomatedRolloutIds
);

export const selectAllAutomatedRollouts = createSelector(
  selectAutomatedRolloutsState,
  fromAutomatedRollouts.selectAllAutomatedRollouts
);

export const selectAutomatedRolloutEntities = createSelector(
  selectAutomatedRolloutsState,
  fromAutomatedRollouts.selectAutomatedRolloutEntities
);

export const selectAutomatedRolloutsLoaded: Selector<object, boolean> = createSelector(
  selectAllAutomatedRollouts,
  (rollouts: AutomatedConfiguration[]) => rollouts?.length > 0
);

export const selectCurrentRollout: Selector<object, AutomatedConfiguration> = createSelector(
  AutomatedRolloutsRouterStoreSelectors.selectRolloutsId,
  selectAutomatedRolloutEntities,
  (rolloutId: string, rollouts: Dictionary<AutomatedConfiguration>) => rollouts[rolloutId]
);

export const selectAllRolloutApplications = createSelector(
  selectCurrentRollout,
  selectAllAppleApplications,
  (rollout, applications) => {
    if (rollout === undefined) return [];

    if (rollout?.applications?.length === 0) {
      return [];
    }

    // Create a set with all rollout applications. this provides O(1) has function, array.includes or find is O(n).
    // we can't use the objects directly since the comparison will use the references, which may not be equal, while the content is.
    const appSet = new Set(rollout.applications.map((application) => applicationString(application)));

    return applications.filter((x) =>
      appSet.has(
        applicationString({
          bundleIdentifier: x.bundleIdentifier,
          version: x.bundleVersion,
        })
      )
    );
  }
);

export const selectAllRolloutAppLocks = createSelector(selectCurrentRollout, (rollout) => {
  return rollout?.appLocks ?? [];
});

export const selectLocationIsPartOfRollout = (locationId: string) =>
  createSelector(
    selectCurrentRollout,
    CategoryStoreSelectors.selectRoomsByLocationId(locationId),
    (rollout, rooms: Room[]) => {
      if (!rollout?.roomIds || !rooms) {
        return false;
      }
      return rooms.filter((room) => rollout.roomIds.includes(room.uniqueId)).length > 0;
    }
  );

function applicationString(application: AutomatedApplicationConfiguration) {
  // bundleIdentifier and version will never contain a slash, therefore this is a safe way to tokenize the object
  return `${application.bundleIdentifier}/${application.version}`;
}
